import React, {useEffect, useState} from 'react';
import commonCss from './css/SurveyStyle.scss'
import PropTypes from 'prop-types';
import SortableTable from "SF/table/SortableTable";
import {Table} from "semantic-ui-react";
import {StudentSurveyPermission} from "./types";
import {unionBy} from "lodash";
import RadioButtonWithoutLabel from "./RadioButtonWithoutLabel";
import {caseInsensitiveSort} from "../../utils/text";

const headers = [
    {
        content: 'Roles',
        sortValue: 'roles',
        width: 6
    }, {
        content: 'None',
        width: 1,
        textAlign: 'center'
    }, {
        content: 'View',
        width: 1,
        textAlign: 'center'
    }, {
        content: 'View & Edit',
        width: 1,
        textAlign: 'center'
    }
];

function StudentSurveyPermissionsTable({roles, surveyPermissions, setProperties}) {
    const [sortDir, setSortDir] = useState('ASC');
    const [sortedRoles, setSortedRoles] = useState([]);

    const sortRoles = (roleList, dir) => (
        dir === 'ASC' ? caseInsensitiveSort(roleList, 'alias') : caseInsensitiveSort(roleList, 'alias').reverse()
    )

    useEffect(() => {
        setSortedRoles(sortRoles(roles, 'ASC'))
    }, [])

    const onSort = (_, dir) => {
        setSortedRoles(sortRoles(sortedRoles, dir));
        setSortDir(dir);
    };

    // Consolidate the permissions chosen by the user for each role for a survey template.
    // In case the user has updated their permission selection for a role, the last one overrides any previous selection(s)
    // since a role can have permission of either None, View or Edit to the survey.
    const changeHandler = (roleId, access) =>
        setProperties({
            permissions: unionBy(
                [{  roleId, access  }],
                surveyPermissions,
                'roleId'
            )
        })

    const isChecked = (selectedRole, access) => {
       return surveyPermissions?.some(permission => permission.roleId === selectedRole && permission.access === access) || false
    }

    // The selectedRole has neither View or Edit access
    const isNoneSelected = (selectedRoleId) =>
        !surveyPermissions?.some(permission => permission.roleId === selectedRoleId && (permission.access === 'view' || permission.access === 'edit'))

    const renderRow = (role, index) => (
        <Table.Row key={role.aliasId} data-testid={`permissionRow-${index}`}>
            <Table.Cell>{role.alias}</Table.Cell>
            <Table.Cell>
                <RadioButtonWithoutLabel name={`${role.alias} permission`}
                                         aria-label={`none for ${role.alias}`}
                                         value={isNoneSelected(role.aliasId)}
                                         onChange={() => changeHandler(role.aliasId, 'none')}
                                         checked={isNoneSelected(role.aliasId)} />
            </Table.Cell>
            <Table.Cell>
                <RadioButtonWithoutLabel name={`${role.alias} permission`}
                                         aria-label={`view for ${role.alias}`}
                                         value={isChecked(role.aliasId, 'view')}
                                         onChange={() => changeHandler(role.aliasId, 'view')}
                                         checked={isChecked(role.aliasId, 'view')} />
            </Table.Cell>
            <Table.Cell>
                <RadioButtonWithoutLabel name={`${role.alias} permission`}
                                         aria-label={`view and edit for ${role.alias}`}
                                         value={isChecked(role.aliasId, 'edit')}
                                         onChange={() => changeHandler(role.aliasId, 'edit')}
                                         checked={isChecked(role.aliasId, 'edit')} />
            </Table.Cell>
        </Table.Row>
    )

    return (
        <SortableTable
            className={commonCss['table']}
            columnToSort={'roles'}
            sortDirection={sortDir}
            onSortChange={onSort}
            headers={headers}
            itemName={'permissions'}
            items={sortedRoles}
            renderItem={renderRow}
            shouldPaginate={false}
            celled
        />
    );
}

StudentSurveyPermissionsTable.propTypes = {
    roles: PropTypes.arrayOf(Object),
    surveyPermissions: PropTypes.arrayOf(StudentSurveyPermission),
    setProperties: PropTypes.func.isRequired
}

export default StudentSurveyPermissionsTable;
